<script setup lang="ts">
import { type LoginConfirmProps, LoginMobileTfaPreCheck, LoginMobileTfaPwdVerify } from '@/api/login'
import { PassControl } from '@mp/component'
import { useUserInfo } from '@mp/hooks'
import { getDeviceCode } from '@mp/utils'
import { useMutation, useQuery } from '@tanstack/vue-query'
import type { FormInstance, FormRules } from 'element-plus'
import { logon } from '@mp/api/gateway/UserControler.js'
import { computed, reactive, ref } from 'vue'
import { ZF_NO } from '@mp/constant'

const props = defineProps<{
  data: LoginConfirmProps
}>()

const { isAdmin } = useUserInfo()
const isPassControl = ref<boolean>(false)
const visible = ref(false)
const formRef = ref<FormInstance>()
const passControlRef = ref<InstanceType<typeof PassControl> | null>(null)
const formModel = reactive<{
  password: string | undefined
}>({
  password: undefined,
})

const formRules = reactive<FormRules>({
  password: [{ required: true, message: '请输入登录密码', trigger: 'blur' }],
})

// #手机验证码登录-双因素认证-密码验证预校验
const { data: preCheckData } = useQuery({
  enabled: computed(() => Boolean(props.data?.twoFactorAuthTicket)),
  queryKey: ['login/mobile/tfa/precheck', computed(() => isPassControl.value)],
  queryFn: () => LoginMobileTfaPreCheck({
    needPwdWidget: isPassControl.value,
    twoFactorAuthTicket: props.data?.twoFactorAuthTicket,
  }),
  refetchOnWindowFocus: false,
})

const pwdWidgetParams = computed(() => preCheckData?.value?.pwdWidget ? JSON.parse(preCheckData?.value?.pwdWidget) : null)

// #登录
const { mutateAsync: mutateLogonAsync } = useMutation({
  mutationFn: logon,
  onSuccess() {
    window.location.replace(`${window.location.origin}/merchant-platform/#/dashboard`)
  },
})

// #验证密码
const { mutate: mutatePwdVerify, isLoading } = useMutation({
  mutationFn: LoginMobileTfaPwdVerify,
  async onSuccess({ accessToken }) {
    if (!accessToken)
      return

    const deviceId = await getDeviceCode()
    sessionStorage.setItem('accessToken', accessToken)
    mutateLogonAsync({
      deviceId,
      appId: ZF_NO,
      accessToken,
    }).catch(() => undefined)
  },
})

async function handleSubmit() {
  // ? 如果使用密码控件登录, 获取值

  const passControlVal = passControlRef.value?.getPassValue()

  if (isPassControl.value)
    formModel.password = passControlVal || undefined

  await formRef.value?.validate()
  mutatePwdVerify({
    loginPwd: isPassControl.value ? '' : formModel.password,
    certSerialNo: pwdWidgetParams.value?.certSerialNo,
    ciphertext: isPassControl.value ? formModel.password : '',
    randomKey: pwdWidgetParams.value?.randomKey,
    twoFactorAuthTicket: props.data?.twoFactorAuthTicket,
  })
}

function handlePassControlChange() {
  formModel.password = undefined
}

function handleCancel() {
  visible.value = false
  formRef.value?.resetFields()
}

defineExpose({
  open() {
    visible.value = true
  },
})
</script>

<template>
  <el-dialog v-model="visible" :close-on-press-escape="false" :close-on-click-modal="false" append-to-body title="身份验证" :width="500" @close="handleCancel">
    <!-- <pre class="pre-code-container" title="props.data">{{ props.data }}</pre>
    <pre class="pre-code-container" title="preCheckData">{{ preCheckData }}</pre>
    <pre class="pre-code-container" title="pwdWidgetParams">{{ pwdWidgetParams }}</pre>
    <pre class="pre-code-container" title="formModel">{{ formModel }}</pre> -->

    <p class=" mb-5 text-n-800 text-sm">
      您已开启双重验证。为了账号安全，请输入登录密码。
    </p>
    <el-form ref="formRef" :model="formModel" :rules="formRules" label-position="top" @submit.prevent="handleSubmit">
      <el-form-item prop="password">
        <template #label>
          <div class="flex-inline">
            登录密码

            <el-popover
              :content="isAdmin ? '请联系商户管理员修改' : '请联系客服修改'"
              placement="top-start"
            >
              <template #reference>
                <el-button type="primary" link size="small" class="ml-2">
                  忘记密码？
                </el-button>
              </template>
            </el-popover>
          </div>
        </template>

        <PassControl
          v-if="isPassControl && pwdWidgetParams?.randomKey"
          ref="passControlRef"
          input-id="login_pass_input_confirm"
          input-container-id="login_pass_input_container_confirm"

          :affine-x="pwdWidgetParams?.affineX"
          :affine-y="pwdWidgetParams?.affineY"
          :pge-r-z-rand-num="pwdWidgetParams?.sKey"
          :pge-r-z-data-b="pwdWidgetParams?.enStr"
          :random-key="pwdWidgetParams?.randomKey ?? ''"
        />
        <el-input v-else v-model="formModel.password" type="password" show-password placeholder="请输入" />
      </el-form-item>

      <el-checkbox v-model="isPassControl" @change="handlePassControlChange">
        <span
          class="text-sm" :class="{
            'text-primary': isPassControl,
            'text-n-90': !isPassControl,
          }"
        >使用密码控件</span>
      </el-checkbox>

      <el-button :loading="isLoading" type="primary" class="w-full mt-4" size="large" @click="handleSubmit">
        确认
      </el-button>
    </el-form>
  </el-dialog>
</template>
